"use client";

import { Scale } from "lucide-react";

import { usePageDescription } from "@/hooks/usePageDescription";

interface TermsAndConditionsContentProps {
  businessId?: string;
  businessConfig?: any;
  lang?: string;
  /** The body the route fetched on the server, so it ships in the HTML. */
  initialContent?: string | null;
}

export function TermsAndConditionsContent({
  businessId,
  lang = "en",
  initialContent,
}: TermsAndConditionsContentProps) {
  const { content: customContent, isLoading } = usePageDescription({
    key: "terms-and-conditions",
    businessId,
    lang,
    initialContent,
  });

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  // If custom content exists, render it
  if (customContent) {
    return (
      <div className="terms-and-conditions-custom-content">
        <div
          className="prose prose-lg max-w-none [&_p:empty]:min-h-[1em] [&_p:empty]:block [&_p>br:only-child]:block prose-p:my-2 prose-headings:mt-6 prose-headings:mb-3 prose-ul:my-3 prose-ol:my-3 prose-li:my-1 prose-blockquote:my-4 prose-p:leading-relaxed"
          dangerouslySetInnerHTML={{ __html: customContent }}
        />
      </div>
    );
  }

  // No content — show empty state
  return (
    <div className="terms-and-conditions-content">
      <div className="flex min-h-[400px] flex-col items-center justify-center text-center">
        <Scale className="mb-4 h-16 w-16 text-gray-300" />
        <h2 className="mb-2 text-xl font-semibold text-gray-500">Terms & Conditions</h2>
        <p className="text-gray-400">
          No terms and conditions have been published yet. Please check back later.
        </p>
      </div>
    </div>
  );
}
